<template>
  <v-text-field
    v-model="inputValue"
    :class="customClass"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :prefix="prefix"
    @input="handleInput"
    @blur="handleBlur"
    autocomplete="off"
    inputmode="decimal"
  />
</template>
<script setup>
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '0.00',
  },
  prefix: {
    type: String,
    default: '$',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: '',
  },
  currency: {
    type: String,
    default: 'USD',
  },
  locale: {
    type: String,
    default: 'en-US',
  },
  // Added new props for number formatting
  minimumFractionDigits: {
    type: Number,
    default: 2,
  },
  maximumFractionDigits: {
    type: Number,
    default: 2,
  },
  numberStyle: {
    type: String,
    default: 'decimal',
    validator: (value) => ['decimal', 'currency', 'percent'].includes(value),
  },
  fallbackFormat: {
    type: Function,
    default: (value) => value.toFixed(2),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const inputValue = ref('');
const rawValue = ref(null);

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';

  if (isNaN(value)) return '';

  try {
    const formatted = new Intl.NumberFormat(props.locale, {
      style: props.numberStyle,
      minimumFractionDigits: props.minimumFractionDigits,
      maximumFractionDigits: props.maximumFractionDigits,
      ...(props.numberStyle === 'currency' ? { currency: props.currency } : {}),
    }).format(value);

    if (props.numberStyle === 'currency') {
      return formatted.replace(/[^\d.,\s]/g, '').trim();
    }

    return formatted;
  } catch (e) {
    console.error('Error formatting currency:', e);
    return props.fallbackFormat(value);
  }
};

const parseInput = (formattedValue) => {
  if (!formattedValue) return null;

  const numericString = formattedValue.replace(/[^\d.-]/g, '');
  const parsedValue = parseFloat(numericString);

  return isNaN(parsedValue) ? null : parsedValue;
};

const handleInput = (event) => {
  const value = event.target.value;

  rawValue.value = parseInput(value);

  if (rawValue.value !== null) {
    inputValue.value = value;
  } else {
    inputValue.value = '';
  }
};

const handleBlur = () => {
  inputValue.value = formatCurrency(rawValue.value);

  emit('update:modelValue', rawValue.value);
  emit('change', rawValue.value);
};

onMounted(() => {
  if (props.modelValue !== null && props.modelValue !== undefined) {
    rawValue.value = props.modelValue;
    inputValue.value = formatCurrency(rawValue.value);
  }
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== rawValue.value) {
      rawValue.value = newVal;
      inputValue.value = formatCurrency(rawValue.value);
    }
  }
);
</script>

<style scoped>
.v-text-field input {
  text-align: right;
}
</style>
